import { useMemo, useCallback, useEffect } from 'react';

/**
 * Get Telegram WebApp instance
 */
export function useTelegram() {
  const webApp = useMemo(() => {
    if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
      return window.Telegram.WebApp;
    }
    return null;
  }, []);

  const initData = useMemo(() => webApp?.initData || '', [webApp]);
  
  const user = useMemo(() => webApp?.initDataUnsafe?.user, [webApp]);
  
  const startParam = useMemo(() => webApp?.initDataUnsafe?.start_param, [webApp]);

  const hapticFeedback = useCallback(
    (type: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'light') => {
      webApp?.HapticFeedback?.impactOccurred(type);
    },
    [webApp]
  );

  const hapticNotification = useCallback(
    (type: 'error' | 'success' | 'warning') => {
      webApp?.HapticFeedback?.notificationOccurred(type);
    },
    [webApp]
  );

  const hapticSelection = useCallback(() => {
    webApp?.HapticFeedback?.selectionChanged();
  }, [webApp]);

  const showMainButton = useCallback(
    (text: string, onClick: () => void) => {
      if (webApp?.MainButton) {
        webApp.MainButton.setText(text);
        webApp.MainButton.onClick(onClick);
        webApp.MainButton.show();
      }
    },
    [webApp]
  );

  const hideMainButton = useCallback(() => {
    webApp?.MainButton?.hide();
  }, [webApp]);

  const showBackButton = useCallback(
    (onClick: () => void) => {
      if (webApp?.BackButton) {
        webApp.BackButton.onClick(onClick);
        webApp.BackButton.show();
      }
    },
    [webApp]
  );

  const hideBackButton = useCallback(() => {
    webApp?.BackButton?.hide();
  }, [webApp]);

  const openLink = useCallback(
    (url: string, tryInstantView = false) => {
      webApp?.openLink(url, { try_instant_view: tryInstantView });
    },
    [webApp]
  );

  const openTelegramLink = useCallback(
    (url: string) => {
      webApp?.openTelegramLink(url);
    },
    [webApp]
  );

  const showAlert = useCallback(
    async (message: string) => {
      if (webApp) {
        await webApp.showAlert(message);
      } else {
        alert(message);
      }
    },
    [webApp]
  );

  const showConfirm = useCallback(
    async (message: string): Promise<boolean> => {
      if (webApp) {
        return webApp.showConfirm(message);
      }
      return confirm(message);
    },
    [webApp]
  );

  const expand = useCallback(() => {
    webApp?.expand();
  }, [webApp]);

  const close = useCallback(() => {
    webApp?.close();
  }, [webApp]);

  const ready = useCallback(() => {
    webApp?.ready();
  }, [webApp]);

  const shareUrl = useCallback(
    (url: string, text?: string) => {
      const shareLink = `https://t.me/share/url?url=${encodeURIComponent(url)}${text ? `&text=${encodeURIComponent(text)}` : ''}`;
      webApp?.openTelegramLink(shareLink);
    },
    [webApp]
  );

  // Set theme colors on mount
  useEffect(() => {
    if (webApp) {
      webApp.setHeaderColor('#1a1a2e');
      webApp.setBackgroundColor('#1a1a2e');
      webApp.expand();
      webApp.ready();
    }
  }, [webApp]);

  return {
    webApp,
    initData,
    user,
    startParam,
    isAvailable: !!webApp,
    colorScheme: webApp?.colorScheme || 'dark',
    viewportHeight: webApp?.viewportHeight || window.innerHeight,

    // Haptic
    hapticFeedback,
    hapticNotification,
    hapticSelection,

    // Main button
    showMainButton,
    hideMainButton,

    // Back button
    showBackButton,
    hideBackButton,

    // Links
    openLink,
    openTelegramLink,

    // Popups
    showAlert,
    showConfirm,

    // Actions
    expand,
    close,
    ready,
    shareUrl,
  };
}
